import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../constants/ZoomAttendance.constants";
import { apiMethods } from "data/api.constants";

/**
 * Custom query hook for fetching booked customers for reconciliation mapping.
 *
 * @param {Object} params - Query parameters.
 * @param {string} params.slotUuid - Slot UUID.
 * @param {string} [params.query=""] - Optional search query string.
 * @returns {Object} Query result object containing customer list and fetching status.
 */
export const useBookedCustomersQuery = ({ slotUuid, query = "" }) => {
  const trimmedQuery = query ? query.trim() : "";
  const isSearchValid =
    !trimmedQuery ||
    trimmedQuery.length >= ZOOM_ATTENDANCE_CONSTANTS.MIN_SEARCH_LENGTH;

  const params = {
    slot_uuid: slotUuid,
    page: ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.INITIAL_PAGE,
    page_size: ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_SIZE_DEFAULT,
  };
  if (trimmedQuery && isSearchValid) {
    params.search = trimmedQuery;
  }

  const queryResult = useCustomQuery({
    url: ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.SEARCH_BOOKED_CUSTOMERS,
    params,
    queryKey: [
      ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.SEARCH_BOOKED_CUSTOMERS,
      slotUuid,
      isSearchValid ? trimmedQuery : "",
    ],
    staleTime: ZOOM_ATTENDANCE_CONSTANTS.QUERY_CACHE_TIME_MS,
    enabled: Boolean(slotUuid),
  });

  return {
    ...queryResult,
    isFetching: Boolean(queryResult.isFetching || queryResult.isLoading),
    customers:
      queryResult.data?.customers || queryResult.data?.data?.customers || [],
  };
};

/**
 * Custom mutation hook for mapping an unrecognized participant to a booked customer.
 *
 * @returns {Object} Mutation hook object.
 */
export const useMapUnrecognizedParticipantMutation = () => {
  return useCustomMutation({
    url: ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.MAP_UNRECOGNIZED_PARTICIPANT,
    method: apiMethods.POST,
  });
};
